import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Users,
  HeartHandshake,
  BadgeCheck,
  Lock,
  Info,
  ChevronDown,
} from 'lucide-react'

// Rosette Trusted Site badge icon
const TrustBadgeIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="h-7 w-7 text-[#2EA7E0] shrink-0"
  >
    <path
      d="M12 2L13.9 3.5C14.4 3.9 15 4 15.6 3.9L18 3.5L18.5 5.9C18.6 6.5 18.9 7 19.4 7.3L21.5 8.5L20.5 10.7C20.2 11.2 20.2 11.8 20.5 12.3L21.5 14.5L19.4 15.7C18.9 16 18.6 16.5 18.5 17.1L18 19.5L15.6 19.1C15 19 14.4 19.1 13.9 19.5L12 21L10.1 19.5C9.6 19.1 9 19 8.4 19.1L6 19.5L5.5 17.1C5.4 16.5 5.1 16 4.6 15.7L2.5 14.5L3.5 12.3C3.8 11.8 3.8 11.2 3.5 10.7L2.5 8.5L4.6 7.3C5.1 7 5.4 6.5 5.5 5.9L6 3.5L8.4 3.9C9 4 9.6 3.9 10.1 3.5L12 2Z"
      fill="currentColor"
    />
    <path
      d="M9.5 14.5L7 12L8.4 10.6L9.5 11.7L13.6 7.6L15 9L9.5 14.5Z"
      fill="white"
    />
  </svg>
)

interface AccordionItem {
  id: number
  title: string
  icon: React.ComponentType<any>
  content: string
}

export default function TravelConfidence() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const accordionItems: AccordionItem[] = [
    {
      id: 1,
      title: 'Reviews Credibility',
      icon: Users,
      content:
        'All our customer reviews are aggregated and verified through automated cross-checking and Trustindex protocols to ensure 100% authenticity and transparent passenger feedback.',
    },
    {
      id: 2,
      title: '99% issue-free services',
      icon: HeartHandshake,
      content:
        'We pride ourselves on our impeccable track record. 99.8% of our private transfers and tours across Austria and Europe are executed smoothly, on schedule, and without any service issues.',
    },
    {
      id: 3,
      title: 'Verified business',
      icon: BadgeCheck,
      content:
        'Salzburg Cab is an officially registered, licensed, and insured private transport carrier complying fully with Austrian and European commercial passenger transport regulations.',
    },
    {
      id: 4,
      title: 'Data protection',
      icon: Lock,
      content:
        'Your booking details, personal credentials, and transaction data are processed via bank-grade secure SSL encryption pipelines, strictly adhering to GDPR guidelines.',
    },
    {
      id: 5,
      title: 'About Trustindex certificate',
      icon: Info,
      content:
        'The Trustindex verification seal is awarded to travel companies that maintain an exceptional, consistent feedback rating and follow honest review practices.',
    },
  ]

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index)
  }

  return (
    <section className="mx-auto max-w-page px-4 py-8 md:py-12 lg:px-[86px] lg:py-16 overflow-hidden">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12 items-center">
        {/* Left Column: Sunset Travel Image */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-6 w-full h-full flex items-center"
        >
          <div className="relative w-full h-[280px] sm:h-[380px] md:h-[450px] lg:h-[610px] overflow-hidden rounded-[32px] shadow-lg group">
            <img
              src="/assets/images/travel_confidence.png"
              alt="Travel with Confidence - Salzburg Cab Private SUV Transfer"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            {/* Soft gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
          </div>
        </motion.div>

        {/* Right Column: Trust Badge Header & Interactive Accordion */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
          className="lg:col-span-6 flex flex-col gap-6"
        >
          {/* Orange Confidence Banner */}
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
            }}
            className="w-full bg-[#FF7027] rounded-[16px] px-6 py-4 lg:py-5 shadow-sm"
          >
            <h2 className="font-host text-[22px] sm:text-2xl lg:text-[26px] font-medium text-black tracking-[-0.015em] leading-tight">
              Travel with Confidence
            </h2>
          </motion.div>

          {/* Description & Muted Trusted Site Box */}
          <motion.div 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
            }}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-1"
          >
            <p className="text-[#494545] font-host text-sm sm:text-base leading-[1.5] max-w-[380px]">
              We are committed to providing safe, reliable, and professional
              transportation services across Austria and Europe.
            </p>
            {/* Trusted Site Box */}
            <div className="flex items-center gap-3 bg-[#ECECEC]/50 border border-gray-200/50 rounded-2xl p-3 shrink-0 self-start sm:self-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
              <div className="flex items-center justify-center p-0.5">
                <TrustBadgeIcon />
              </div>
              <div className="flex flex-col">
                <span className="text-[12px] font-bold text-[#1E293B] leading-tight font-poppins">
                  Trusted Site
                </span>
                <span className="text-[10px] text-gray-500 font-medium font-poppins mt-0.5">
                  Verified by Trustindex
                </span>
              </div>
            </div>
          </motion.div>

          {/* Accordion List */}
          <div className="flex flex-col gap-3">
            {accordionItems.map((item, index) => {
              const IconComponent = item.icon
              const isOpen = activeIndex === index

              return (
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
                  }}
                  key={item.id}
                  className="bg-white border border-gray-100 rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.03)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:border-gray-200/80 transition-all duration-300 cursor-pointer"
                  onClick={() => toggleAccordion(index)}
                >
                  {/* Accordion Header */}
                  <div className="flex items-center justify-between p-4 sm:p-5">
                    <div className="flex items-center gap-3.5">
                      <div className="p-1 rounded-lg text-text-dark shrink-0">
                        <IconComponent className="h-[21px] w-[21px] text-[#323232]" />
                      </div>
                      <span className="font-host font-semibold text-text-dark text-sm sm:text-base tracking-[-0.01em]">
                        {item.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#0D9488] font-host font-bold text-xs sm:text-sm tracking-wide">
                        Certified
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 text-text-dark transition-transform duration-300 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>

                  {/* Accordion Content Panel (Smooth Grid Row Expansion) */}
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen
                        ? 'grid-rows-[1fr] opacity-100 border-t border-gray-50'
                        : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="p-4 sm:p-5 pt-3 sm:pt-4 text-xs sm:text-sm text-[#796C6C] leading-relaxed font-host pl-[52px]">
                        {item.content}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
